import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchGlobalSettings, saveGlobalSettings, testIpInfoToken } from '../api/admin';
import { Snackbar } from '@mui/material';

export function SettingsPage() {
    const queryClient = useQueryClient();
    const [logRetentionDays, setLogRetentionDays] = useState(60);
    const [ipInfoToken, setIpInfoToken] = useState('');
    const [showToken, setShowToken] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    
    // Test Token States
    const [isTestingToken, setIsTestingToken] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const showMessage = (msg: string) => {
        setSnackbarMessage(msg);
        setSnackbarOpen(true);
    };

    const maskToken = (token: string) => {
        if (!token) return '';
        if (token.length <= 6) return '*'.repeat(token.length);
        return token.substring(0, 4) + '*'.repeat(token.length - 6) + token.substring(token.length - 2);
    };

    const { data: settingsData, isLoading, error } = useQuery({
        queryKey: ['global-settings'],
        queryFn: fetchGlobalSettings,
    });

    // Update local state when settings data is loaded
    useEffect(() => {
        if (settingsData) {
            if (typeof settingsData.logRetentionDays === 'number') {
                setLogRetentionDays(settingsData.logRetentionDays);
            }
            if (settingsData.networkInsights && typeof settingsData.networkInsights.ipInfoToken === 'string') {
                setIpInfoToken(settingsData.networkInsights.ipInfoToken);
            } else {
                setIpInfoToken('');
            }
        }
    }, [settingsData]);

    const saveMutation = useMutation({
        mutationFn: saveGlobalSettings,
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['global-settings'] });
            showMessage('Settings updated successfully');
        },
        onError: (err) => {
            console.error('Error saving settings:', err);
            showMessage('Failed to update settings');
        }
    });

    const handleSave = () => {
        saveMutation.mutate({
            logRetentionDays,
            networkInsights: {
                ipInfoToken
            }
        });
    };

    const handleCancel = () => {
        if (settingsData) {
            if (typeof settingsData.logRetentionDays === 'number') {
                setLogRetentionDays(settingsData.logRetentionDays);
            }
            if (settingsData.networkInsights && typeof settingsData.networkInsights.ipInfoToken === 'string') {
                setIpInfoToken(settingsData.networkInsights.ipInfoToken);
            } else {
                setIpInfoToken('');
            }
        } else {
            setLogRetentionDays(60);
            setIpInfoToken('');
        }
        showMessage('Changes discarded');
    };

    const handleTestToken = async () => {
        if (!ipInfoToken) {
            showMessage('Please enter a token to test');
            return;
        }

        setIsTestingToken(true);
        try {
            const result = await testIpInfoToken(ipInfoToken);
            if (result.success) {
                showMessage('Token working successfully!');
            } else {
                showMessage(`Token validation failed: ${result.error}`);
            }
        } catch (err: any) {
            console.error('Test token failed:', err);
            showMessage(err.response?.data?.error || 'Validation failed');
        } finally {
            setIsTestingToken(false);
        }
    };

    if (isLoading) {
        return <div className="text-center py-8 text-gray-500">Loading settings...</div>;
    }

    if (error) {
        return <div className="text-center py-8 text-red-500">Error loading settings</div>;
    }

    return (
        <div className="max-w-3xl mx-auto space-y-6">
            <div className="pl-1">
                <div className="text-[10px] tracking-[0.25em] text-[#c87a53] uppercase font-bold mb-1">
                    SYSTEM CONFIGURATION
                </div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Settings
                </h1>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Configure machine licensing defaults and log retention settings.
                </p>
            </div>

            {/* Logs & Audit Card */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-150/40 dark:border-gray-700/60 p-6 space-y-6">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        Logs & Audit
                    </h2>
                    <p className="text-xs text-gray-400">
                        Operational logs and history cleanup settings.
                    </p>
                </div>

                <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800/60">
                        <div>
                            <label htmlFor="log-retention" className="block text-sm font-medium text-gray-800 dark:text-gray-250">
                                Keep activity logs for
                            </label>
                            <p className="text-xs text-gray-400 mt-1">
                                Activity logs older than this limit will be deleted.
                            </p>
                        </div>
                        <div className="flex-shrink-0">
                            <select
                                id="log-retention"
                                value={logRetentionDays}
                                onChange={(e) => setLogRetentionDays(Number(e.target.value))}
                                className="w-full sm:w-44 px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 text-sm"
                            >
                                <option value={60}>60 Days (Default)</option>
                                <option value={90}>90 Days</option>
                                <option value={120}>120 Days</option>
                                <option value={180}>180 Days</option>
                                <option value={365}>365 Days</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Machine Network Intelligence Section */}
                <div className="border-t border-gray-150 dark:border-gray-700/60 pt-6 space-y-4">
                    <div>
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                            Machine Network Intelligence
                        </h3>
                        <p className="text-xs text-gray-400">
                            Used for ISP, ASN, and country insights for machine IP addresses.
                        </p>
                    </div>

                    <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800/60 space-y-4">
                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="ipinfo-token" className="block text-sm font-medium text-gray-800 dark:text-gray-250">
                                IPInfo Token (Optional)
                            </label>
                            <div className="relative flex items-center">
                                <input
                                    id="ipinfo-token"
                                    type={showToken || isFocused ? 'text' : 'password'}
                                    value={showToken || isFocused ? ipInfoToken : maskToken(ipInfoToken)}
                                    onChange={(e) => setIpInfoToken(e.target.value)}
                                    onFocus={() => setIsFocused(true)}
                                    onBlur={() => setIsFocused(false)}
                                    placeholder="Leave empty to use system default token"
                                    className="w-full pl-3 pr-10 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 text-sm font-mono"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowToken(!showToken)}
                                    className="absolute right-3 text-gray-400 hover:text-gray-650 dark:hover:text-gray-300 cursor-pointer focus:outline-none"
                                >
                                    {showToken ? (
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            <p className="text-[11px] text-gray-400 mt-1">
                                Optional. If empty, system default provider token will be used.
                            </p>
                        </div>

                        <div className="flex justify-start">
                            <button
                                type="button"
                                onClick={handleTestToken}
                                disabled={isTestingToken || !ipInfoToken}
                                className="px-4 py-2 bg-gray-150 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-650 text-gray-700 dark:text-gray-200 text-xs font-semibold rounded-xl transition-colors disabled:opacity-50 cursor-pointer"
                            >
                                {isTestingToken ? 'Testing...' : 'Test Token'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Actions */}
            <div className="flex justify-end gap-3 pt-2">
                <button
                    onClick={handleCancel}
                    disabled={saveMutation.isPending}
                    className="px-5 py-2.5 bg-gray-150 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-650 text-gray-700 dark:text-gray-200 rounded-xl text-sm font-medium transition-colors disabled:opacity-50 cursor-pointer"
                >
                    Cancel
                </button>
                <button
                    onClick={handleSave}
                    disabled={saveMutation.isPending}
                    className="px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2 shadow-sm cursor-pointer"
                >
                    {saveMutation.isPending ? 'Saving...' : 'Save Settings'}
                </button>
            </div>

            {/* Toast Notification */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </div>
    );
}
